import {
  VideoPresets,
  type TrackPublishDefaults,
  type VideoCaptureOptions,
} from 'livekit-client';

export type VideoQualityProfile = 'medium' | 'high' | 'max';

export const VIDEO_QUALITY_STORAGE_KEY = 'evolutto-meet-video-quality';
export const VIDEO_QUALITY_CHANGED_EVENT = 'evolumeet:video-quality-changed';

export type VideoQualitySearchParams = {
  hq?: string;
  quality?: string;
};

export type VideoQualityRoomCapture = {
  videoCaptureDefaults: VideoCaptureOptions;
  publishDefaults: Pick<
    TrackPublishDefaults,
    'videoEncoding' | 'videoSimulcastLayers'
  >;
};

const PROFILE_LABELS: Record<VideoQualityProfile, string> = {
  medium: 'Média',
  high: 'Alta',
  max: 'Máxima',
};

export function isVideoQualityProfile(value: unknown): value is VideoQualityProfile {
  return value === 'medium' || value === 'high' || value === 'max';
}

export function getDefaultVideoQualityProfile(): VideoQualityProfile {
  const envDefault = process.env.NEXT_PUBLIC_DEFAULT_VIDEO_QUALITY?.trim().toLowerCase();
  if (isVideoQualityProfile(envDefault)) {
    return envDefault;
  }
  return 'medium';
}

export function resolveVideoQualityFromSearchParams(
  params: VideoQualitySearchParams,
): VideoQualityProfile | null {
  if (params.hq === 'true') {
    return 'max';
  }
  const raw = params.quality?.trim().toLowerCase();
  if (isVideoQualityProfile(raw)) {
    return raw;
  }
  return null;
}

export function readVideoQualityProfile(): VideoQualityProfile {
  if (typeof window === 'undefined') {
    return getDefaultVideoQualityProfile();
  }
  try {
    const stored = window.localStorage.getItem(VIDEO_QUALITY_STORAGE_KEY);
    if (isVideoQualityProfile(stored)) {
      return stored;
    }
  } catch {
    // ignore storage errors
  }
  return getDefaultVideoQualityProfile();
}

export function writeVideoQualityProfile(profile: VideoQualityProfile): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(VIDEO_QUALITY_STORAGE_KEY, profile);
  window.dispatchEvent(
    new CustomEvent(VIDEO_QUALITY_CHANGED_EVENT, { detail: { profile } }),
  );
}

export function getVideoQualityLabel(profile: VideoQualityProfile): string {
  return PROFILE_LABELS[profile];
}

/**
 * Com modo economia ativo, o perfil efetivo da câmera nunca passa de medium
 * (mesmo que localStorage tenha high/max). Não altera o valor persistido.
 */
export function getEffectiveVideoQualityProfile(
  stored: VideoQualityProfile,
  economyEnabled: boolean,
): VideoQualityProfile {
  if (!economyEnabled) return stored;
  if (stored === 'high' || stored === 'max') return 'medium';
  return stored;
}

export function getVideoQualityRoomCapture(profile: VideoQualityProfile): VideoQualityRoomCapture {
  switch (profile) {
    case 'high':
      return {
        videoCaptureDefaults: {
          resolution: VideoPresets.h720.resolution,
        },
        publishDefaults: {
          videoEncoding: {
            ...VideoPresets.h720.encoding,
            maxBitrate: 1_300_000,
            maxFramerate: 25,
          },
          videoSimulcastLayers: [VideoPresets.h360, VideoPresets.h180],
        },
      };
    case 'max':
      return {
        videoCaptureDefaults: {
          resolution: VideoPresets.h1080.resolution,
        },
        publishDefaults: {
          videoEncoding: {
            ...VideoPresets.h1080.encoding,
            maxBitrate: 1_650_000,
            maxFramerate: 30,
          },
          videoSimulcastLayers: [VideoPresets.h720, VideoPresets.h360],
        },
      };
    case 'medium':
    default:
      return {
        videoCaptureDefaults: {
          resolution: VideoPresets.h540.resolution,
        },
        publishDefaults: {
          videoEncoding: {
            ...VideoPresets.h540.encoding,
            maxBitrate: 850_000,
            maxFramerate: 24,
          },
          videoSimulcastLayers: [VideoPresets.h360, VideoPresets.h180],
        },
      };
  }
}

/** Constraints de captura para applyConstraints após economy/large-room/background. */
export function getVideoCaptureMediaConstraints(
  profile: VideoQualityProfile,
): MediaTrackConstraints {
  switch (profile) {
    case 'high':
      return {
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        frameRate: { max: 30 },
      };
    case 'max':
      return {
        width: { ideal: 1920, max: 1920 },
        height: { ideal: 1080, max: 1080 },
        frameRate: { max: 30 },
      };
    case 'medium':
    default:
      return {
        width: { ideal: 960, max: 1280 },
        height: { ideal: 540, max: 720 },
        frameRate: { max: 24 },
      };
  }
}
